/**
 * Booklet spreads (Publisher's Two-Page Spread). Pure helpers — no React, no editor state — shared by the canvas, the
 * Pages pane, keyboard navigation and Export / Print.
 *
 * Pairing: page 1 alone on the right, then (2,3) (4,5) …; an even last page alone on the left.
 * 0-based: index 0 → right with no partner; odd → left page, partner i+1 (if any); even > 0 → right page, partner i-1.
 */

/** Facing page of page `i` in a booklet of `count` pages. Returns { facingIndex | null, currentOnRight }. */
export const spreadPartner = (i, count, enabled = true) => {
  if (!enabled || count < 2) return { facingIndex: null, currentOnRight: false };
  if (i === 0) return { facingIndex: null, currentOnRight: true };
  if (i % 2 === 1) return { facingIndex: i + 1 < count ? i + 1 : null, currentOnRight: false };
  return { facingIndex: i - 1, currentOnRight: true };
};

/** The spreads of a booklet as rows of page indexes: [[0], [1,2], [3,4], …] (or one page per row when disabled). */
export const spreadRows = (count, enabled = true) => {
  const rows = [];
  if (!enabled) { for (let i = 0; i < count; i += 1) rows.push([i]); return rows; }
  if (count > 0) rows.push([0]);
  for (let i = 1; i < count; i += 2) rows.push(i + 1 < count ? [i, i + 1] : [i]);
  return rows;
};

/** Index of the spread row that contains page `i`. */
export const spreadRowIndexOf = (i, count, enabled = true) => spreadRows(count, enabled).findIndex((row) => row.includes(i));

/**
 * The page Page Down / Page Up should land on. In spread view it jumps a whole spread (landing on the left-hand page of
 * the next / previous spread, or page 1); in single-page view it steps one page. `step` = +1 / -1.
 */
export const flipTarget = (currentIndex, count, step, spreadView) => {
  if (count < 2) return currentIndex;
  if (!spreadView) return Math.max(0, Math.min(count - 1, currentIndex + step));
  const rows = spreadRows(count, true);
  const r = rows.findIndex((row) => row.includes(currentIndex));
  const next = Math.max(0, Math.min(rows.length - 1, r + step));
  return rows[next][0];
};

const pageLabel = (i) => `Page ${i + 1}`;

/**
 * Sheets to export / print for the selected page indexes.
 * - layout 'single' → one sheet per page: { kind: 'page', page, pageIndex, width, height, label, pageIndexes }
 * - layout 'spread' → one sheet per spread (a spread is included when *either* of its pages is selected, so "current
 *   page" prints the whole spread as displayed): { kind: 'spread', left, right, leftIndex, rightIndex, spine, width,
 *   height, label, pageIndexes }. Page 1 and an even last page keep their blank half (width = two pages).
 */
export const buildSheets = (pages, indices, layout = 'single') => {
  const sel = new Set(indices);
  if (layout !== 'spread' || pages.length < 2) {
    return indices.filter((i) => pages[i]).map((i) => ({ kind: 'page', page: pages[i], pageIndex: i, width: pages[i].width, height: pages[i].height, label: pageLabel(i), pageIndexes: [i] }));
  }
  const sheets = [];
  spreadRows(pages.length, true).forEach((row) => {
    if (!row.some((i) => sel.has(i))) return;
    const leftIndex = row[0] === 0 ? null : row[0];
    const rightIndex = row[0] === 0 ? 0 : row.length === 2 ? row[1] : null;
    const left = leftIndex !== null ? pages[leftIndex] : null;
    const right = rightIndex !== null ? pages[rightIndex] : null;
    const lw = (left || right).width, rw = (right || left).width;
    const spine = left ? left.width : right.width; // x where the right-hand page starts
    const width = (left ? left.width : rw) + (right ? right.width : lw);
    const height = Math.max(left?.height || 0, right?.height || 0);
    const pageIndexes = row.slice();
    const label = row.length === 2 ? `Pages ${row[0] + 1}–${row[1] + 1}` : pageLabel(row[0]);
    sheets.push({ kind: 'spread', left, right, leftIndex, rightIndex, spine, width, height, label, pageIndexes });
  });
  return sheets;
};

/** File-name suffix for a sheet: 'page3', 'pages2-3' or 'sheet1-front'. */
export const sheetFileSuffix = (sheet) => {
  if (sheet.booklet) return `sheet${sheet.booklet.sheet}-${sheet.booklet.side}`;
  return sheet.pageIndexes.length === 2 ? `pages${sheet.pageIndexes[0] + 1}-${sheet.pageIndexes[1] + 1}` : `page${sheet.pageIndexes[0] + 1}`;
};

/* ------------------------------------------------------------------------------------------------------------------
 * Booklet imposition (saddle stitch): every sheet of paper carries two pages per side, is printed on both sides, and
 * the stack is folded once and stapled at the spine. For the folded booklet to read 1, 2, 3 … the page count is padded
 * to a multiple of 4 and sheet k (0-based) gets  front = [n-1-2k | 2k]  and  back = [2k+1 | n-2-2k]  — for 8 pages:
 * 8|1, 2|7, 6|3, 4|5. Printed two-sided this needs "flip on the short edge" (landscape sheets), or print the fronts
 * first, turn the stack over and print the backs.
 * ---------------------------------------------------------------------------------------------------------------- */

/** Booklet sides in print order. `sides`: 'both' (interleaved front, back, …), 'front' or 'back'. */
export const bookletSides = (count, sides = 'both') => {
  const total = Math.max(4, Math.ceil(count / 4) * 4);
  const list = [];
  for (let k = 0; k < total / 4; k += 1) {
    const front = { sheet: k + 1, side: 'front', left: total - 1 - 2 * k, right: 2 * k };
    const back = { sheet: k + 1, side: 'back', left: 2 * k + 1, right: total - 2 - 2 * k };
    if (sides !== 'back') list.push(front);
    if (sides !== 'front') list.push(back);
  }
  return { list, total, blanks: total - count, sheets: total / 4 };
};

/**
 * Booklet sheets (spread-kind, see buildSheets) in print order; indexes past the last page are blank halves.
 * Each sheet carries `booklet: { sheet, side, total, blanks, sheets }` and a label like "Sheet 1 front · pages 8 | 1".
 */
export const buildBookletSheets = (pages, sides = 'both') => {
  if (!pages.length) return [];
  const { list, total, blanks, sheets } = bookletSides(pages.length, sides);
  const base = pages[0];
  const name = (i) => (i < pages.length ? String(i + 1) : 'blank');
  return list.map((s) => {
    const left = pages[s.left] || null, right = pages[s.right] || null;
    const lw = (left || right || base).width, rw = (right || left || base).width;
    return {
      kind: 'spread', left, right, leftIndex: left ? s.left : null, rightIndex: right ? s.right : null, spine: lw,
      width: lw + rw, height: Math.max((left || base).height, (right || base).height),
      label: `Sheet ${s.sheet} ${s.side} · pages ${name(s.left)} | ${name(s.right)}`,
      pageIndexes: [s.left, s.right].filter((i) => i < pages.length),
      booklet: { sheet: s.sheet, side: s.side, total, blanks, sheets },
    };
  });
};
